<template>
  <transition name="fade">
    <template>
      <div class="field-group">
        <label :for="controlId" class="animated hasContent" v-if="!fullWidth && !displayInline && !!label">
          <span class="fixed-label new">{{ label }}</span>
          <span class="animated-label">{{ label }}</span>
        </label>
        <div
          class="date-wrapper fill-width"
          v-bind:class="{
            'loading': calendarDataUpdating,
            'multipleDates': multipleDates,
            'full-width-calendar': fullWidth,
            'displayInline': displayInline,
          }"
        >
          <input
            type="text"
            ref="dateinput"
            class="datepicker-here fill-width attribute-field"
            :value="formattedDate"
            :name="controlId"
            :id="controlId"
            :placeholder="textFieldPlaceholder"
            :class="{ hidden: multipleDates || displayInline }"
            readonly
          />
          <input
            type="text"
            class="hidden"
            v-if="multipleDates && multipleDatesControlId"
            v-model="multipleDatesValue"
            :name="multipleDatesControlId"
            :id="multipleDatesControlId"
          />
        </div>
        <input type="text" class="hidden" :id="controlId + '_day'" :name="controlId + '_day'" v-model="selectedDay" />
        <input
          type="text"
          class="hidden"
          :id="controlId + '_month'"
          :name="controlId + '_month'"
          v-model="selectedMonth"
        />
        <input
          type="text"
          class="hidden"
          :id="controlId + '_year'"
          :name="controlId + '_year'"
          v-model="selectedYear"
        />
      </div>
    </template>
  </transition>
</template>
<script>
// We use a customized version of air-datepicker that defaults to English instead of Russian
import '@nop-js/air-datepicker'

import moment from 'moment'

// @NOTE TCW-2243
//  default air-datepicker language is `ru`.
//  These configs could be better

// @NOTE this must be in `language-country` format (lower-upper)
const defaultDatepickerLanguage = 'en-US'
// @NOTE these must be lowercase to match script names
const whitelistedLocales = ['en-us', 'en-au', 'en-ca', 'en-nz']

export default {
  name: 'form-date-picker',
  model: {
    event: 'value-changed',
  },
  data() {
    return {
      selectedDay: null,
      selectedMonth: null,
      selectedYear: null,
      multipleDatesValue: '',
      dateKey: null,
    }
  },
  mounted() {
    this.initialize()
    if (this.value) {
      // Set the datepicker to the value
      const datePicker = $(this.$refs.dateinput).data('datepicker')
      if (typeof this.value === 'string') {
        // Adding T00:00:00 to the end of the date string causes the date to be parsed
        // as a local date instead of UTC. This is necessary in order to select the correct date.
        const dateStr = this.value.endsWith('Z') ? this.value : this.value + 'T00:00:00'
        datePicker.selectDate(new Date(dateStr))
      } else {
        datePicker.selectDate(this.value)
      }
    }
  },
  computed: {
    multipleDatesSetting() {
      let setting = false
      if (this.multipleDates) {
        setting = this.numberOfDates && this.numberOfDates > 1 ? this.numberOfDates : true
      }

      return setting
    },

    textFieldPlaceholder() {
      if (this.locale.language === 'en-US') return 'mm/dd/yyyy'

      return 'dd/mm/yyyy'
    },
    formattedDate() {
      if (!this.dateKey) return undefined

      const [year, month, day] = this.dateKey.split('-')
      return this.textFieldPlaceholder.replace('dd', day).replace('mm', month).replace('yyyy', year)
    },
  },
  methods: {
    initialize() {
      const startingDate = this.defaultDate ? new Date(this.defaultDate) : this.initialDate
      const startingMonth = startingDate.getMonth() + 1
      const startingYear = startingDate.getFullYear()

      this.selectedDay = startingDate.getDate()
      this.selectedMonth = startingDate.getMonth() + 1
      this.selectedYear = startingDate.getFullYear()

      const datepickerLanguage = whitelistedLocales.includes(this.locale.language.toLowerCase())
        ? this.locale.language
        : defaultDatepickerLanguage

      $(this.$refs.dateinput).datepicker({
        language: datepickerLanguage,
        autoClose: true,
        startDate: startingDate,
        minDate: this.minDate,
        maxDate: this.maxDate,
        multipleDates: this.multipleDatesSetting,
        multipleDatesSeparator: this.multipleDatesSeparator,
        inline: this.fullWidth || this.multipleDates || this.displayInline,
        toggleSelected: true,
        selectOtherMonths: false,
        onSelect: this.onDateSelect,
        onRenderCell: this.renderCell,
        onChangeMonth: this.onChangeMonth,
        onHide: this.onHide,
      })

      if (this.defaultDate) {
        $(this.$refs.dateinput).data('datepicker').selectDate(this.defaultDate)
      }
    },
    onChangeMonth(month, year) {
      this.selectedMonth = month + 1
      this.selectedYear = year
      this.$emit('month-changed', { selectedMonth: month + 1, selectedYear: year })
    },

    onDateSelect(formattedDate, date, inst) {
      if (date) {
        var newDate = Array.isArray(date) ? date[0] : date
        var attributeDateList = Array.isArray(date) ? date : new Array(date)
        this.selectedDay = newDate ? newDate.getDate() : null
        this.selectedMonth = newDate ? newDate.getMonth() + 1 : null
        this.selectedYear = newDate ? newDate.getFullYear() : null

        if (this.multipleDates) {
          this.multipleDatesValue = attributeDateList
            .map((aDate) => {
              return moment(aDate).format('YYYY-MM-DD')
            })
            .join('|')
        }

        // Make sure date-changed is fired before value changed.
        // Some usages will trigger updates based on value-changed and any dates should be processed first.
        const keyYear = this.selectedYear
        const keyMonth = _.padStart(this.selectedMonth, 2, '0')
        const keyDate = _.padStart(this.selectedDay, 2, '0')
        const dateKey = `${keyYear}-${keyMonth}-${keyDate}`
        this.dateKey = dateKey

        this.$emit('date-changed', {
          date: dateKey,
          dateList: attributeDateList,
          month: this.selectedMonth,
          day: this.selectedDay,
          year: this.selectedYear,
        })
        this.$emit('value-changed', dateKey)
      }
    },

    onHide(inst, animationCompleted) {
      // This callback will fire 2x - once at the start of the animation, one at the end
      if (animationCompleted) {
        if (this.displayInline || this.multipleDates) {
          return
        }
        if (!this.value) {
          let newDate = this.defaultDate ? new Date(this.defaultDate) : this.initialDate
          const month = newDate.getMonth()
          const year = newDate.getFullYear()
          this.$emit('month-changed', { selectedMonth: month + 1, selectedYear: year })
          this.viewDatePickerDate(newDate)
        } else {
          let newDate = new Date(`${this.dateKey}T00:00:00`)
          this.viewDatePickerDate(newDate)
        }
      }
    },

    onRenderCell(date, cellType) {
      return this.renderCell(date, cellType)
    },

    reinitializeDatePicker() {
      $(this.$refs.dateinput).data('datepicker')?.update({
        inline: true,
      })
    },

    viewDatePickerDate(viewDate) {
      let datePicker = $(this.$refs.dateinput)
      if (datePicker) {
        datePicker.data('datepicker').date = viewDate
      }
    },
  },
  watch: {
    calendarDataHash() {
      this.reinitializeDatePicker()
    },

    initialDate(fromVal, toVal) {
      if (this.initialDate && fromVal && toVal && fromVal?.toDateString() !== toVal?.toDateString()) {
        this.viewDatePickerDate(this.initialDate)
      }
    },

    value(toValue) {
      if (!toValue) {
        $(this.$refs.dateinput).data('datepicker').clear()
      }
    },

    calendarDataUpdating: {
      immediate: true,
      handler(toValue) {
        if (toValue) {
          $('#datepickers-container').addClass('loading')
        } else {
          $('#datepickers-container').removeClass('loading')
        }
      },
    },
  },
  props: {
    calendarDataHash: {
      type: String,
      default: '000',
    },

    calendarDataUpdating: {
      type: Boolean,
      default: false,
    },

    controlId: {
      type: String,
      default: 'datepicker_date',
    },

    defaultDate: {
      type: Date,
      default: null,
    },

    displayInline: {
      type: Boolean,
      default: false,
    },

    fullWidth: {
      type: Boolean,
      default: false,
    },

    initialDate: {
      type: Date,
      default: () => new Date(),
    },

    label: {
      type: String,
      default: null,
    },

    locale: {
      type: Object,
      default() {
        return {
          language: 'en-US',
          currency: 'USD',
        }
      },
    },

    minDate: {
      type: Date,
    },

    maxDate: {
      type: Date,
    },

    multipleDates: {
      type: Boolean,
      defaut: false,
    },

    multipleDatesControlId: {
      type: String,
      default: null,
    },

    multipleDatesSeparator: {
      type: String,
      default: '|',
    },

    numberOfDates: {
      type: Number,
      default: 1,
    },

    placeholder: {
      type: String,
      default: null,
    },

    renderCell: {
      type: Function,
      default: function (date, cellType) {
        var html = `<span class="basic-date">${date.getDate()}</span>`

        if (cellType === 'day') {
          return {
            html: html,
          }
        }
      },
    },

    value: {
      type: String,
    },
  },
}
</script>
